import React, {forwardRef, useState, useCallback} from 'react'
import styled, {css} from 'styled-components'
import RuleItemInnerVisualSlot from '@fuegokit/react/experimental/RuleBuilder/_RuleItemInnerVisualSlot'
import {generateUniqueKey} from '@fuegokit/react/experimental/RuleBuilder/utils'
import {Appearance} from '@fuegokit/react/components/AtlasBadge'
import {RuleItemProps, Spacing} from './types'
import {
  useRefObjectAsForwardedRef,
  Box,
  Stack,
  BoxProps,
  sx,
  SxProp,
  themeGet,
  AtlasBadge,
  useOutsideClick
} from '../..'
import {ForwardRefComponent as PolymorphicForwardRefComponent} from '../../utils/polymorphic'

const CounterBadge = styled(AtlasBadge)``

const spacingMap: Record<Spacing, string> = {
  compact: '40px',
  relaxed: '84px',
  default: '48px',
  none: ''
}

const borderStylesMap = {
  selected: themeGet('colors.border.selected'),
  default: 'transparent'
}

const Wrapper = styled(Box)<BoxProps & RuleItemProps & SxProp>`
  border-radius: ${themeGet('radii.2')};

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet('space.4')};
  width: 100%;
  // dynamic spacing
  padding: ${p => (p.spacing === 'none' ? '' : themeGet('space.3'))};
  max-height: ${p => spacingMap[p.spacing as Spacing] || ''};

  background-color: ${p =>
    p.disabled ? themeGet('colors.background.disabled') : themeGet('colors.background.neutral.default.[default]')};
  color: ${p => (p.disabled ? themeGet('colors.text.disabled') : themeGet('colors.text.default'))};
  border: 1px solid;
  border-color: ${themeGet('colors.border.default')};
  transition: background-color 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  transition: ${p => (p.isSelected ? 'revert' : '350ms')};
  border-color: ${p => (p.isSelected ? themeGet('colors.border.selected') : 'transparent')};
  user-select: none;

  // @TODO: once rule builder icons are stable and shipped into Fuegoicons, we should only need to target the svg
  .rule-item-visual {
    svg,
    rect {
      fill: ${p => (p.disabled ? themeGet('colors.icon.disabled') : '')};
    }
  }

  &:hover {
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
    border: ${p => (p.isSelected ? '1px solid' : '1px solid')};
    border-color: ${p => (p.isSelected ? borderStylesMap.selected : borderStylesMap.default)};

    cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  }
  ${p =>
    p.isSelected &&
    css`
      &:hover {
        box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
        transition: background-color 0.1s ease-out, box-shadow 0.45s cubic-bezier(0.47, 0.03, 0.49, 1.38);
      }
    `}
  &:focus {
    border-color: ${themeGet('colors.border.focused')};
  }
  &:active {
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
    border: 1px solid ${themeGet('colors.border.selected')};
  }

  ${sx}
`

/**
 *
 * A rule item is used to describe an item in a workflow and provides actions a user can take.
 *
 * There are several variants; each variant will compose the Standard rule builder.
 * (Standard) Rule Item. Selectable. No collapse. Can potentially be dismissed.
 * (Collapsible) Rule Item. Collapsible. Can potentially be selected. No dismiss. Not draggable.
 * (Draggable) Rule Item. Draggable. Can potentially be dismissed. Can potentially be collapsed.
 *
 * spacing: 'compact' (max-height: 40px) | 'default' (max-height: 48px) | 'relaxed' (max-height: 84px) | 'none' (0 padding; consumers can add with sx prop)
 *
 */
const RuleItem = forwardRef(
  (
    {
      id,
      children,
      loading,
      counter,
      spacing = 'default',
      loaderPosition = 'auto',
      leadingVisual: LeadingVisual,
      trailingVisual: TrailingVisual,
      trailingAction,
      disabled: disabledProp,
      isSelected: isSelectedProp,
      sx: sxProp,
      as = 'div',
      onSelect,
      testId,
      ...props
    },
    forwardedRef
  ) => {
    const wrapperTestId = `${testId}-wrapper`
    const containerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, containerRef)

    const showLeadingLoadingIndicator =
      loading && (loaderPosition === 'leading' || Boolean(LeadingVisual && loaderPosition !== 'trailing'))
    const showTrailingLoadingIndicator =
      loading && (loaderPosition === 'trailing' || Boolean(loaderPosition === 'auto' && !LeadingVisual))

    const [isActive, setIsActive] = useState(isSelectedProp)
    const [isDisabled, setIsDisabled] = useState(disabledProp)

    // const isRuleItemSelected = isSelectedProp || isActive
    const isRuleItemSelected = isSelectedProp
    const isRuleItemDisabled = disabledProp || isDisabled

    // function handleRuleItemSelect() {
    //   setIsActive(true)
    // }

    const handleRuleItemSelect = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (isSelectedProp) return

        if (onSelect) {
          // onSelect(event, event.clientY)
          onSelect(event)
        }
      },
      [isSelectedProp, onSelect]
    )

    function handleRuleItemBlur() {
      setIsActive(false)
    }
    useOutsideClick({onClickOutside: handleRuleItemBlur, containerRef})

    let appearanceValue
    if (isRuleItemSelected) {
      appearanceValue = 'primary'
    } else if (isRuleItemDisabled) {
      appearanceValue = 'disabled'
    } else {
      appearanceValue = 'default'
    }
    return (
      <Wrapper
        as={as}
        id={id}
        spacing={spacing}
        ref={containerRef}
        isSelected={isRuleItemSelected}
        disabled={isRuleItemDisabled}
        onClick={handleRuleItemSelect}
        sx={sxProp}
        data-testid={testId ? wrapperTestId : 'undefined'}
        data-selected={isRuleItemSelected}
      >
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
          <RuleItemInnerVisualSlot
            visualPosition="leading"
            showLoadingIndicator={showLeadingLoadingIndicator}
            hasLoadingIndicator={typeof loading === 'boolean'}
          >
            {typeof LeadingVisual === 'function' ? <LeadingVisual /> : LeadingVisual}
          </RuleItemInnerVisualSlot>
          <Stack direction="row" size={4} sx={{alignItems: 'center'}}>
            {React.Children.map(React.Children.toArray(children), (child, idx) => {
              if (!child) {
                return null
              }
              const uniqueKey = generateUniqueKey()

              return (
                <React.Fragment key={uniqueKey}>
                  {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                  {spacing ? React.cloneElement(child as JSX.Element, {spacing}) : child}
                </React.Fragment>
              )
            })}
          </Stack>
        </Box>
        <RuleItemInnerVisualSlot
          visualPosition="trailing"
          showLoadingIndicator={showTrailingLoadingIndicator}
          hasLoadingIndicator={typeof loading === 'boolean'}
        >
          {typeof TrailingVisual === 'function' ? <TrailingVisual /> : TrailingVisual}
          {counter && (
            // eslint-disable-next-line no-nested-ternary
            <CounterBadge appearance={appearanceValue as Appearance}>{counter}</CounterBadge>
          )}

          {trailingAction ? React.cloneElement(trailingAction as JSX.Element, {isRuleItemDisabled}) : null}
        </RuleItemInnerVisualSlot>
      </Wrapper>
    )
  }
) as PolymorphicForwardRefComponent<'span' | 'div' | 'button', RuleItemProps & SxProp>

RuleItem.displayName = 'RuleItem'
export default RuleItem
